import { useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import API from "../api/api";
import { BACKEND_URL } from "../api/api";
import { documentPaths } from "../utils/documents";

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("nl-BE");
};

export default function CustomerDetail() {
  const { id } = useParams();
  const location = useLocation();
  const includeArchived = new URLSearchParams(location.search).get("archived") === "true";
  const [previewImage, setPreviewImage] = useState(null);

  const { data: customer, isLoading, isError } = useQuery({
    queryKey: ["customers", id, includeArchived],
    queryFn: async () => (await API.get(`/customers/${id}${includeArchived ? "?includeArchived=true" : ""}`)).data
  });

  const docs = useMemo(() => documentPaths(customer?.documents), [customer?.documents]);

  if (isLoading) return <div className="loading-state">Klantendossier laden...</div>;
  if (isError || !customer) return <div className="error-state">Klantendossier laden mislukt</div>;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <Link className="text-link" to="/customers">Terug naar klanten</Link>
          <h1 className="page-title">{customer.firstName} {customer.lastName}</h1>
          <p className="page-subtitle">
            Klantendossier met documenten, reservaties en schadegevallen.
            {customer.archivedAt ? ` Gearchiveerd op ${formatDate(customer.archivedAt)}.` : ""}
          </p>
        </div>
      </header>

      <div className="reservation-workbench">
        <div className="panel panel-pad">
          <h2 className="detail-title">Klantgegevens</h2>
          <div className="detail-grid">
            <div className="detail-item"><div className="detail-label">Telefoon</div><div className="detail-value">{customer.phone || "-"}</div></div>
            <div className="detail-item"><div className="detail-label">E-mail</div><div className="detail-value">{customer.email || "-"}</div></div>
            <div className="detail-item"><div className="detail-label">Adres</div><div className="detail-value">{customer.address || "-"}</div></div>
            <div className="detail-item"><div className="detail-label">Factuur</div><div className="detail-value">{customer.wantsInvoice ? `Ja - ${customer.vatNumber || "-"}` : "Nee"}</div></div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Documenten</h2>
            {docs.length === 0 ? (
              <div className="empty-state">Geen documenten gevonden.</div>
            ) : (
              <div className="photo-grid">
                {docs.map((path) => (
                  <button key={path} className="reservation-row" onClick={() => setPreviewImage(path)}>
                    <img className="photo-thumb" src={`${BACKEND_URL}${path}`} alt="Document" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="panel panel-pad detail-panel">
          <div className="detail-section">
            <h2 className="detail-title">Reservaties</h2>
            <div className="list">
              {customer.reservations?.length === 0 && <div className="empty-state">Geen reservaties gevonden.</div>}
              {customer.reservations?.map((reservation) => (
                <Link key={reservation.id} className="list-row reservation-row" to={`/reservations?reservationId=${reservation.id}`}>
                  <div>
                    <div className="row-title">{reservation.trailer?.name || `Trailer #${reservation.trailerId}`}</div>
                    <div className="row-meta">{formatDate(reservation.startDate)} tot {formatDate(reservation.endDate)} - {reservation.status}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Schadegevallen</h2>
            <div className="list">
              {customer.damageCases?.length === 0 && <div className="empty-state">Geen schadegevallen gevonden.</div>}
              {customer.damageCases?.map((damageCase) => (
                <div key={damageCase.id} className="list-row">
                  <div>
                    <div className="row-title">{damageCase.trailer?.name || "Geen trailer"}</div>
                    <div className="row-meta">{damageCase.status} - EUR {damageCase.damageCost || 0}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {previewImage && (
        <button className="image-modal" onClick={() => setPreviewImage(null)}>
          <img src={`${BACKEND_URL}${previewImage}`} alt="Document groot" />
        </button>
      )}
    </section>
  );
}
